import restClient from "api/restClient";
import { Button } from "components/Button/Button";
import { Modal } from "components/Modal/Modal";
import NotificationService from "components/Notification/Notification.service";
import RestErrorNotification from "components/Notification/RestErrorNotification";
import SimpleNotification from "components/Notification/SimpleNotification";
import { Object_Template } from "graphql/generated/graphql";
import { useCallback, useEffect, useState } from "react";
import {
    attributesFingerprint,
    getHookStampRows,
    hasStampableHook,
    looksLikeStampedSchema,
    StampResult,
} from "./hookInputSchemaStamp.helpers";
import "./HookInputSchemaStamp.scss";

interface HookInputSchemaStampProps {
    resourceData: Object_Template;
    /** Called after a successful stamp so the caller can refetch the record. */
    fetchResourceData: () => void;
}

/**
 * Baseline attribute fingerprint per object template, kept at MODULE scope on
 * purpose. The panel is remounted while the object-template record refetches
 * after an attribute edit (the ResourcesSingle screen tears the layout subtree
 * down and back up around a refetch), so component-instance refs are reset to the
 * post-edit state and never see the change. A module-level store survives that
 * remount, so the first fingerprint recorded for a template (on initial load)
 * still differs from the post-edit fingerprint and the change is detected. Keyed
 * by object_template id; the entry is refreshed whenever we (re)establish a
 * baseline or after we react to a change.
 */
const baselineFingerprintByTemplate = new Map<string, string>();

/**
 * Cross-hook controller for the object-template screen: it watches the template's
 * attribute set and, when an in-session change makes a previously-stamped hook
 * schema go out of date, prompts the author to re-stamp the affected hook flows
 * (no live binding — a one-shot prompt that also warns the hook flow's script
 * must be updated to match). It renders no visible section of its own — the
 * per-hook one-click "Stamp input schema" buttons live next to each hook flow
 * picker in the form above (see HookStampButton). Only the re-stamp prompt modal
 * surfaces, and only when a stamped hook schema is stale.
 * See operator-task-ot-lifecycle-hook-one-click-input-schema.
 */
export const HookInputSchemaStamp = ({
    resourceData,
    fetchResourceData,
}: HookInputSchemaStampProps) => {
    const objectTemplateId = resourceData?.id;
    const rows = getHookStampRows(resourceData);
    // Slice 3 (OT-attribute-change prompt): when the template's attributes change
    // and a stamped hook schema goes out of date, prompt the author to re-stamp.
    const [staleConfirm, setStaleConfirm] = useState<StampResult[] | null>(null);
    const [isUpdatingStale, setIsUpdatingStale] = useState(false);
    const fingerprint = attributesFingerprint(resourceData);

    // React to an in-session change of the object template's attribute set: if a
    // hook flow carries a stamped-looking input schema that is now out of date,
    // ask the author whether to re-stamp it. No live binding — a one-shot prompt,
    // and it warns that the hook flow's script must be updated to match.
    useEffect(() => {
        if (!objectTemplateId) {
            return; // record not loaded yet
        }
        // First time we see this template this session (initial load, before any
        // edit): record the baseline fingerprint and do NOT prompt. The baseline
        // lives at module scope so it survives the panel being remounted while the
        // record refetches after an edit — otherwise the post-edit remount would
        // re-baseline to the new attributes and the change would never be seen.
        const baseline = baselineFingerprintByTemplate.get(objectTemplateId);
        if (baseline === undefined) {
            baselineFingerprintByTemplate.set(objectTemplateId, fingerprint);
            return;
        }
        if (baseline === fingerprint) {
            return; // attributes unchanged
        }
        baselineFingerprintByTemplate.set(objectTemplateId, fingerprint);

        const boundHooks = getHookStampRows(resourceData).filter((row) => row.flow);
        if (!boundHooks.length) {
            return;
        }
        let cancelled = false;
        Promise.all(
            boundHooks.map((hook) =>
                restClient
                    .post(
                        `/object_template/${objectTemplateId}/stamp_hook_input_schema?by=id`,
                        { hook_type: hook.type, apply: false }
                    )
                    .then((response) => response?.data as StampResult)
                    .catch(() => null)
            )
        ).then((results) => {
            if (cancelled) {
                return;
            }
            const stale = results.filter(
                (res): res is StampResult =>
                    !!res && res.changed && looksLikeStampedSchema(res.current_input_schema)
            );
            if (stale.length) {
                setStaleConfirm(stale);
            }
        });
        return () => {
            cancelled = true;
        };
        // resourceData is read only to derive the bound hooks; `fingerprint`
        // already captures the attribute changes we react to, so we key the
        // effect on it + the template id intentionally.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [objectTemplateId, fingerprint]);

    const handleUpdateStale = useCallback(() => {
        if (!objectTemplateId || !staleConfirm) {
            return;
        }
        setIsUpdatingStale(true);
        Promise.all(
            staleConfirm.map((res) =>
                restClient.post(
                    `/object_template/${objectTemplateId}/stamp_hook_input_schema?by=id`,
                    { hook_type: res.hook_type, apply: true }
                )
            )
        )
            .then(() => {
                NotificationService.success(
                    <SimpleNotification
                        title="Hook input schemas updated"
                        subtitle="Remember to update each hook flow's script to use the new schema."
                    />
                );
                setStaleConfirm(null);
                fetchResourceData();
            })
            .catch((error) => {
                NotificationService.error(
                    <RestErrorNotification
                        titleOverride="Could not update the hook input schemas"
                        error={error}
                    />
                );
            })
            .finally(() => setIsUpdatingStale(false));
    }, [objectTemplateId, staleConfirm, fetchResourceData]);

    if (!hasStampableHook(rows)) {
        return null;
    }

    return (
        <Modal
            isVisible={!!staleConfirm}
            onCancel={() => (isUpdatingStale ? undefined : setStaleConfirm(null))}
            title="Hook input schemas may be out of date"
            width="560px"
            closable={false}
            destroyOnClose
        >
            {staleConfirm && (
                <div className="hookInputSchemaStamp__confirm">
                    <p className="hookInputSchemaStamp__confirmText">
                        This object template's attributes changed. The stamped input schema of the
                        following hook flow{staleConfirm.length > 1 ? "s" : ""} no longer matches
                        the template and may cause schema-validation errors:
                    </p>
                    <ul className="hookInputSchemaStamp__staleList">
                        {staleConfirm.map((res) => (
                            <li key={res.hook_type}>
                                <strong>{res.hook_type}</strong> → {res.flow_name}
                            </li>
                        ))}
                    </ul>
                    <p className="hookInputSchemaStamp__warning">
                        ⚠ Updating the input schema does not change the hook flow's code. After
                        updating, edit each hook flow's script to use the new schema.
                    </p>
                    {/*
                      Canonical modal button row (ui-design-principle-modals-dialogs
                      reference impl): neutral "Not now" on the LEFT, primary confirm
                      on the RIGHT. No corner close "×".
                    */}
                    <div className="hookInputSchemaStamp__actions">
                        <Button
                            label="Not now"
                            variation="white"
                            onClick={() => setStaleConfirm(null)}
                            disabled={isUpdatingStale}
                        />
                        <Button
                            label={
                                staleConfirm.length > 1
                                    ? "Update input schemas"
                                    : "Update input schema"
                            }
                            variation="primary"
                            onClick={handleUpdateStale}
                            loading={isUpdatingStale}
                            disabled={isUpdatingStale}
                        />
                    </div>
                </div>
            )}
        </Modal>
    );
};
